// Single source of truth for React Query keys.
//
// Query-key strings were re-typed as inline literals across 30+ hooks, and the
// same key was defined more than once (`inrange-agents-uploads-tree` /
// `inrange-agents-uploads-list` each lived in two hooks). That drift is a real
// bug source: a mutation that must invalidate a sibling hook's cache has to
// hardcode that hook's literal, and a mismatch silently leaves a stale view -
// e.g. `useUploadDelete` didn't invalidate the uploads-TREE key, so a deleted
// Drive-sourced file lingered in the tree. Centralising the keys here (typed factories) lets cross-hook
// invalidations reference ONE definition, so they can't disagree.
//
// EVERY React Query key lives here (W9 consolidation
// complete): no inline key literal remains anywhere under `web/src`. `QK.<name>`
// is the root string (first tuple element); `queryKeys.<name>(...)` is the typed
// factory for keys that carry trailing arguments. A key that is only ever used
// prefix-only / no-arg has just a `QK` entry, referenced as `[QK.<name>]`.

// Root key strings (the first tuple element). Exported so a hook that
// invalidates by PREFIX only (all projects) can reference the ONE definition
// instead of re-declaring the literal - this is what stops the same key living
// in two hooks and drifting.
export const QK = {
  uploadsList: "inrange-agents-uploads-list",
  uploadsTree: "inrange-agents-uploads-tree",
  projectGet: "inrange-agents-project-get",
  projectsList: "inrange-agents-projects-list",
  amazonOverview: "inrange-agents-amazon-overview",
  amazonBlueprint: "inrange-agents-amazon-blueprint",
  amazonBuildingNotes: "inrange-agents-amazon-building-notes",
  amazonOwners: "inrange-agents-amazon-owners",
  amazonItemHistory: "inrange-agents-amazon-item-history",
  amazonChanges: "inrange-agents-amazon-changes",
  amazonEusdrQuestions: "inrange-agents-amazon-eusdr-questions",
  sharesMe: "inrange-agents-shares-me",
  shareList: "inrange-agents-share-list",
  refreshAllSitesActive: "inrange-agents-refresh-all-sites-active",
  uploadExtracted: "inrange-agents-upload-extracted",
  uploadsExtractedBatch: "inrange-agents-uploads-extracted-batch",
  uploadExtractedText: "inrange-agents-upload-extracted-text",
  replyContext: "inrange-agents-reply-context",
  procurement: "inrange-agents-procurement",
  driveActiveSync: "inrange-agents-drive-active-sync",
  driveBrowse: "inrange-agents-drive-browse",
  costSummary: "inrange-agents-cost-summary",
  costLineItems: "inrange-agents-cost-line-items",
  operationsDashboard: "inrange-agents-operations-dashboard",
  agentStateList: "inrange-agents-agent-state-list",
  agentStateDocument: "inrange-agents-agent-state-document",
  agentStateDiff: "inrange-agents-agent-state-diff",
  integrationsList: "inrange-agents-integrations-list",
  mailboxReadLog: "inrange-agents-mailbox-read-log",
  diagnosticsGrants: "inrange-agents-diagnostics-grants",
  integrationsCount: "inrange-agents-integrations-count",
  googleAuthStatus: "inrange-agents-google-auth-status",
  googleSharedDrives: "inrange-agents-google-shared-drives",
  globalDataList: "inrange-agents-global-data-list",
  globalDriveFiles: "inrange-agents-global-drive-files",
  globalDriveConnection: "inrange-agents-global-drive-connection",
  globalDriveBrowse: "inrange-agents-global-drive-browse",
  driveFolderTree: "inrange-agents-drive-folder-tree",
  bmaSitesList: "inrange-agents-bma-sites-list",
  bmaSiteGet: "inrange-agents-bma-site-get",
  bmaOrgsList: "inrange-agents-bma-orgs-list",
  asyncOperation: "inrange-agents-async-operation",
  aiTraceGet: "inrange-agents-ai-trace-get",
  statefulTurnTrace: "inrange-agents-stateful-turn-trace",
  projectMailboxes: "inrange-agents-project-mailboxes",
  slackStatus: "inrange-agents-slack-status",
  projectSlack: "inrange-agents-project-slack",
  slackScopeSubscriptions: "inrange-agents-slack-scope-subscriptions",
  slackDayDocument: "inrange-agents-slack-day-document",
  actionHistory: "inrange-agents-action-history",
  queuedPass: "inrange-agents-queued-pass",
  uploadsDescribe: "uploads-describe",
  agentAttention: "stateful-agent-attention",
  runsDescribe: "stateful-runs-describe",
  dashboard: "inrange-agents-dashboard",
  projectAttributes: "inrange-agents-project-attributes",
  reviewComments: "inrange-agents-review-comments",
  briefingVersions: "inrange-agents-briefing-versions",
  settingsHistory: "inrange-agents-settings-history",
  feedbackQuestions: "inrange-agents-feedback-questions",
  projectAgentStatus: "inrange-agents-project-agent-status",
  agentPortfolio: "inrange-agents-agent-portfolio",
  teamSettings: "inrange-agents-team-settings",
  projectEnrolment: "inrange-agents-project-enrolment",
  statefulAgents: "inrange-agents-stateful-agents",
  statefulPrompt: "inrange-agents-stateful-prompt",
  statefulRuns: "inrange-agents-stateful-runs",
  statefulRun: "inrange-agents-stateful-run",
  dayManifest: "inrange-agents-day-manifest",
  agentRuns: "inrange-agents-agent-runs",
  agentStateDocuments: "inrange-agents-agent-state-documents",
  globalDataText: "inrange-agents-global-data-text",
  unreadAnswers: "inrange-agents-unread-answers",
  register: "inrange-agents-register",
  registersForDocuments: "inrange-agents-registers-for-documents",
  projectRegisters: "inrange-agents-project-registers",
  globalRegisters: "inrange-agents-global-registers",
  clauseComments: "inrange-agents-clause-comments",
} as const;

export const queryKeys = {
  // One document's register, read for the file alone.
  register: (documentId: string, registerId: string | null) =>
    [QK.register, documentId, registerId] as const,
  // The newest register of each document in a set, keyed by the sorted ids.
  registersForDocuments: (idsKey: string) =>
    [QK.registersForDocuments, idsKey] as const,
  // A project's register lines for each agent that assesses them.
  projectRegisters: (projectId: string, agent: string | null) =>
    [QK.projectRegisters, projectId, agent] as const,
  // Every Global data file with a clause list.
  globalRegisters: () => [QK.globalRegisters] as const,
  clauseComments: (documentId: string) => [QK.clauseComments, documentId] as const,
  // Every agent's state-document revision history for one project.
  agentStateList: (projectId: string | null) =>
    [QK.agentStateList, projectId] as const,
  // One revision of one agent's document; version null = the current one.
  agentStateDocument: (
    projectId: string | null,
    agent: string | null,
    version: number | null,
  ) => [QK.agentStateDocument, projectId, agent, version] as const,
  // A diff between two revisions; fromVersion null = against empty.
  agentStateDiff: (
    projectId: string | null,
    agent: string | null,
    fromVersion: number | null,
    toVersion: number | null,
  ) => [QK.agentStateDiff, projectId, agent, fromVersion, toVersion] as const,
  // Per-project flat uploads list (useUploadsList).
  uploadsList: (projectId: string) => [QK.uploadsList, projectId] as const,
  // Per-project synced-files TREE (Drive/Gmail synced-files panels).
  uploadsTree: (projectId: string) => [QK.uploadsTree, projectId] as const,
  // Single project by id (useProject); also used prefix-only via [QK.projectGet].
  projectGet: (projectId: string | null) =>
    [QK.projectGet, projectId] as const,
  // Projects list scoped by customer code; also prefix-only via [QK.projectsList].
  projectsList: (customerCode: string | null) =>
    [QK.projectsList, customerCode] as const,
  // Single upload extraction view (useUploadExtracted).
  uploadExtracted: (uploadId: string | null) =>
    [QK.uploadExtracted, uploadId] as const,
  // Batched extraction views keyed by a stable signature of the id set
  // (useUploadsExtracted). Invalidate prefix-only via [QK.uploadsExtractedBatch]
  // to refresh whatever batch is mounted regardless of its id set.
  uploadsExtractedBatch: (idsKey: string) =>
    [QK.uploadsExtractedBatch, idsKey] as const,
  // Single upload kept-text view (useUploadExtractedText).
  uploadExtractedText: (uploadId: string | null) =>
    [QK.uploadExtractedText, uploadId] as const,
  // Who a reply on one email thread copies (useReplyContext).
  replyContext: (uploadId: string) => [QK.replyContext, uploadId] as const,
  // Procurement result scoped by project; also prefix-only via [QK.procurement].
  procurement: (projectId: string | null) =>
    [QK.procurement, projectId] as const,
  // Active Drive sync operation key for a project.
  driveActiveSync: (projectId: string) =>
    [QK.driveActiveSync, projectId] as const,
  // Prefix of every browse or search of what a project's Drive sync reaches.
  driveBrowse: (projectId: string) => [QK.driveBrowse, projectId] as const,
  // Active Gmail sync operation key for a project.
  // Gmail relevance stats for a project (nullable project id).
  // Cost summary over an optional (start, end, days) window.
  costSummary: (
    start: string | null,
    end: string | null,
    days: number | null,
  ) => [QK.costSummary, start, end, days] as const,
  // Every inbox a project draws from (useProjectMailboxes).
  projectMailboxes: (projectId: string | null) =>
    [QK.projectMailboxes, projectId] as const,
  // The Slack channels a project sees, directly or by scope (useProjectSlack).
  projectSlack: (projectId: string | null) =>
    [QK.projectSlack, projectId] as const,
  slackDayDocument: (globalDataId: string | null) =>
    [QK.slackDayDocument, globalDataId] as const,
  // Drive folder tree keyed by folder id.
  driveFolderTree: (folderId: string | null) =>
    [QK.driveFolderTree, folderId] as const,
  // One BMA site by id.
  bmaSiteGet: (siteId: string | null) => [QK.bmaSiteGet, siteId] as const,
  // Async operation status keyed by operation_key.
  asyncOperation: (operationKey: string | null) =>
    [QK.asyncOperation, operationKey] as const,
  // One AI trace by (surface, artefactId).
  aiTraceGet: (surface: string | null, artefactId: string | null) =>
    [QK.aiTraceGet, surface, artefactId] as const,
} as const;
